/**
 * Admin Services Management Page
 * View and manage all client services
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Server,
  Search,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  RefreshCw,
  Edit,
  Trash2,
  PlayCircle,
  StopCircle,
  Plus,
  Filter,
} from 'lucide-react';

interface Service {
  id: number;
  clientId: number;
  clientName: string;
  clientEmail: string;
  productId: number;
  productName: string;
  domain?: string;
  username?: string;
  status: 'pending' | 'active' | 'suspended' | 'terminated' | 'cancelled';
  billingCycle: string;
  recurringAmount: number;
  nextDueDate: string;
  registrationDate: string;
}

const statusConfig = {
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/20',
  },
  active: {
    label: 'Active',
    icon: CheckCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
  },
  suspended: {
    label: 'Suspended',
    icon: AlertCircle,
    color: 'text-orange-400',
    bgColor: 'bg-orange-900/20',
  },
  terminated: {
    label: 'Terminated',
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-900/20',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
  },
};

const ServicesPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadServices();
  }, [filterStatus]);

  const loadServices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      const response = await fetch(`/api/v1/admin/services?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setServices(data.data.services);
      }
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter((service) => {
    const searchMatch =
      searchQuery === '' ||
      service.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.domain?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.id.toString().includes(searchQuery);

    return searchMatch;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getBillingCycleLabel = (cycle: string) => {
    const labels: Record<string, string> = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannually: 'Semi-Annually',
      annually: 'Annually',
      biennially: 'Biennially',
      triennially: 'Triennially',
    };
    return labels[cycle] || cycle;
  };

  const handleSuspendService = async (serviceId: number) => {
    if (!confirm('Are you sure you want to suspend this service?')) return;

    try {
      await fetch(`/api/v1/admin/services/${serviceId}/suspend`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadServices();
    } catch (error) {
      console.error('Failed to suspend service:', error);
      alert('Failed to suspend service');
    }
  };

  const handleUnsuspendService = async (serviceId: number) => {
    if (!confirm('Are you sure you want to unsuspend this service?')) return;

    try {
      await fetch(`/api/v1/admin/services/${serviceId}/unsuspend`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadServices();
    } catch (error) {
      console.error('Failed to unsuspend service:', error);
      alert('Failed to unsuspend service');
    }
  };

  const handleTerminateService = async (serviceId: number) => {
    if (
      !confirm(
        'Are you sure you want to TERMINATE this service? This action cannot be undone and will delete all data.'
      )
    )
      return;

    try {
      await fetch(`/api/v1/admin/services/${serviceId}/terminate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadServices();
    } catch (error) {
      console.error('Failed to terminate service:', error);
      alert('Failed to terminate service');
    }
  };

  const monthlyRecurringRevenue = services
    .filter((s) => s.status === 'active')
    .reduce((sum, s) => {
      const monthlyAmount =
        s.billingCycle === 'monthly'
          ? s.recurringAmount
          : s.billingCycle === 'annually'
          ? s.recurringAmount / 12
          : s.recurringAmount / 3;
      return sum + monthlyAmount;
    }, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Server className="w-8 h-8" />
            Services Management
          </h1>
          <p className="text-gray-400 mt-2">Manage all client services and provisioning</p>
        </div>
        <Link
          to="/admin/services/create"
          className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 flex items-center gap-2 font-medium shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40"
        >
          <Plus className="w-5 h-5" />
          Create Service
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Services</p>
              <p className="text-2xl font-bold text-white mt-1">{services.length}</p>
            </div>
            <Server className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {services.filter((s) => s.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {services.filter((s) => s.status === 'pending').length}
              </p>
            </div>
            <Clock className="w-10 h-10 text-yellow-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Suspended</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">
                {services.filter((s) => s.status === 'suspended').length}
              </p>
            </div>
            <AlertCircle className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">MRR</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">
                {formatPrice(monthlyRecurringRevenue)}
              </p>
            </div>
            <RefreshCw className="w-10 h-10 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by product, client, domain, or username..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto">
          {['all', 'active', 'pending', 'suspended', 'terminated', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                filterStatus === status
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                  : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-700/50'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Services Table */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-gray-400">Loading services...</span>
          </div>
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <Server className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Services Found</h3>
          <p className="text-gray-400">
            {searchQuery ? 'No services match your search.' : 'No services in the system yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-gray-300 font-medium">ID</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Client</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Product</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Domain/Username</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Billing</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Status</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Next Due</th>
                  <th className="text-right p-4 text-gray-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredServices.map((service) => {
                  const statusInfo = statusConfig[service.status];
                  const StatusIcon = statusInfo.icon;

                  return (
                    <tr key={service.id} className="hover:bg-slate-700/30">
                      <td className="p-4">
                        <span className="text-gray-400 font-mono text-sm">#{service.id}</span>
                      </td>
                      <td className="p-4">
                        <Link
                          to={`/admin/clients/${service.clientId}`}
                          className="text-white hover:text-orange-400"
                        >
                          {service.clientName}
                        </Link>
                        <div className="text-xs text-gray-400">{service.clientEmail}</div>
                      </td>
                      <td className="p-4">
                        <Link
                          to={`/admin/products/${service.productId}`}
                          className="text-white hover:text-orange-400"
                        >
                          {service.productName}
                        </Link>
                      </td>
                      <td className="p-4">
                        {service.domain && (
                          <div className="text-white">{service.domain}</div>
                        )}
                        {service.username && (
                          <div className="text-gray-400 text-sm">{service.username}</div>
                        )}
                        {!service.domain && !service.username && (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-white font-medium">
                          {formatPrice(service.recurringAmount)}
                        </div>
                        <div className="text-xs text-gray-400">
                          {getBillingCycleLabel(service.billingCycle)}
                        </div>
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="p-4 text-gray-300">
                        {formatDate(service.nextDueDate)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/services/${service.id}`}
                            className="p-2 text-orange-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          {service.status === 'active' && (
                            <button
                              onClick={() => handleSuspendService(service.id)}
                              className="p-2 text-orange-400 hover:bg-slate-700/50 rounded transition-colors"
                              title="Suspend"
                            >
                              <StopCircle className="w-4 h-4" />
                            </button>
                          )}
                          {service.status === 'suspended' && (
                            <button
                              onClick={() => handleUnsuspendService(service.id)}
                              className="p-2 text-green-400 hover:bg-slate-700/50 rounded transition-colors"
                              title="Unsuspend"
                            >
                              <PlayCircle className="w-4 h-4" />
                            </button>
                          )}
                          {(service.status === 'active' || service.status === 'suspended') && (
                            <button
                              onClick={() => handleTerminateService(service.id)}
                              className="p-2 text-red-400 hover:bg-slate-700/50 rounded transition-colors"
                              title="Terminate"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
